import currency from 'currency.js';
import lscache from 'lscache';
import fx from 'money';
import exchangeRates from 'utils/exchangeRates';

import currencyClient from '../flux/clients/currency';

const defaultCurrency = 'EUR';

fx.base = defaultCurrency;
fx.rates = exchangeRates;
const RATESKEY = 'RATES';
const BASEKEY = 'BASE';
const RATESKEYV2 = 'RATES_V2';
const BASEKEYV2 = 'BASE_V2';
const CACHEDURATION = 3 * 60; //minutes
const CACHEDURATIONV2 = 60; //minutes
const CURRENCIES_CACHE_KEY = 'CURRENCIES_LIST';
const symbolsSuffix = {
  LKR: 'LKR',
  NPR: 'NPR',
  SGD: 'SGD',
};
const getCachedCurrencies = () => {
  return lscache.get(CURRENCIES_CACHE_KEY) || [];
};
const isInteger = function (value: number) {
  return (
    typeof value === 'number' && isFinite(value) && Math.floor(value) === value
  );
};

const getNumberFormat = function (price: any, rounded: boolean) {
  const num = parseFloat(price);
  if (rounded) {
    return Math.ceil(num);
  }
  if (isFinite(num)) {
    if (isInteger(num)) {
      return num;
    }
    return num.toFixed(2);
  }

  return num;
};
const getPrettyPrice = function (
  priceInput: number,
  currencyCode = defaultCurrency,
  rounded = false,
): string {
  const price = priceInput > 0 ? priceInput : 0.0;
  const cachedCurrencies = getCachedCurrencies();
  const foundCurrency = cachedCurrencies.find(
    (cur) => cur.code === currencyCode,
  );
  const currency = foundCurrency ? foundCurrency.symbol_native : currencyCode;
  const symbol = symbolsSuffix[currencyCode] ? symbolsSuffix[currencyCode] : '';
  return currency + getNumberFormat(price, rounded) + symbol;
};

const getV1CurrencyRatesInternal = async () => {
  try {
    const json = await currencyClient.getV1CurrencyRates();
    fx.rates = json.rates;
    fx.base = json.base;
    lscache.set(RATESKEY, json.rates, CACHEDURATION);
    lscache.set(BASEKEY, json.base, CACHEDURATION);
  } catch (error) {
    console.error('Error fetching V1 currency rates:', error);
  }
};

const getV2CurrencyRates = async (targetCurrency: string) => {
  try {
    const json = await currencyClient.getV2CurrencyRates(targetCurrency);
    lscache.set(RATESKEYV2, json.rates, CACHEDURATIONV2);
    lscache.set(BASEKEYV2, json.base, CACHEDURATIONV2);
  } catch (error) {
    console.error('Error fetching V2 currency rates:', error);
  }
};

const getConvertedPrice = function (
  priceInput: number,
  currencyFromStr = defaultCurrency,
  currencyToStr = defaultCurrency,
): number {
  const price = priceInput > 0 ? priceInput : 0.0;
  if (currencyFromStr === currencyToStr) {
    return priceInput;
  }

  const currencyFrom = currency(price);
  const rate = lscache.get(RATESKEYV2)?.[currencyToStr];
  if (!!rate) {
    return currencyFrom.divide(rate).value;
  }

  void getV2CurrencyRates(currencyFromStr);

  return price;
};

const getPrettyPriceFromOtherCurrency = function (
  value: number,
  currencyCode: string,
  otherCurrencyCode: string,
  round = false,
): string {
  const convertedPrice = getConvertedPrice(
    value,
    currencyCode,
    otherCurrencyCode,
  );
  return getPrettyPrice(convertedPrice, otherCurrencyCode, round);
};

const ratesFromCache = lscache.get(RATESKEY);

const getV1CurrencyRates = () => {
  if (ratesFromCache?.[defaultCurrency]) {
    try {
      fx.rates = ratesFromCache;
      fx.base = lscache.get(BASEKEY) || defaultCurrency;
    } catch (e) {
      console.error('Error setting V1 currency rates from cache:', e);
      void getV1CurrencyRatesInternal();
    }
  } else {
    void getV1CurrencyRatesInternal();
  }
};

getV1CurrencyRates();
void getV2CurrencyRates(defaultCurrency);

export default {
  getPrettyPrice: getPrettyPrice,
  getConvertedPrice: getConvertedPrice,
  getPrettyPriceFromOtherCurrency: getPrettyPriceFromOtherCurrency,
};
